import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, MessageCircle, Clock3, UserX, EyeOff,
  Zap, Brain, Send, UserCheck, Users, ShieldCheck, TrendingUp,
  Building2, Lock, Headphones,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import DemoDialog from "@/components/site/DemoDialog";
import AmbientParticles from "@/components/site/AmbientParticles";
import RelatedSolutions from "@/components/site/RelatedSolutions";
import IllustrativePill from "@/components/site/IllustrativePill";
import StandardCTA from "@/components/site/StandardCTA";
import { BROKERAGES_IMAGES } from "@/lib/site";

const CHALLENGES = [
  { icon: MessageCircle, title: "Leads lost in WhatsApp/inbox", desc: "Valuable inquiries get buried in conversations and slip through the cracks." },
  { icon: Clock3,        title: "Slow response to inquiries",  desc: "Delays in responding mean hot leads go cold — and competitors win." },
  { icon: UserX,         title: "No structured follow-up",      desc: "Inconsistent follow-up leads to lost opportunities and lower conversion." },
  { icon: EyeOff,        title: "No visibility into what's working", desc: "Without clear insights, it's impossible to optimize and scale what drives results." },
];

const SOLUTIONS = [
  { icon: Zap,        title: "Instant lead capture across channels", desc: "Capture every inquiry in real time — from every channel, in one place." },
  { icon: Brain,      title: "AI qualification & scoring",            desc: "AI scores and qualifies leads instantly so your team focuses on high-intent prospects." },
  { icon: Send,       title: "Automated multi-touch follow-up",       desc: "Intelligent, automated follow-ups keep leads engaged and warm across every touchpoint." },
  { icon: UserCheck,  title: "Smart routing to the right agent",      desc: "Leads are routed to the best-fit agent for faster response and higher conversion." },
];

const RESULTS = [
  { icon: Clock3,       title: "Faster response times",   desc: "Respond instantly and never miss a hot lead." },
  { icon: Users,        title: "More qualified viewings", desc: "Focus on serious buyers with higher intent." },
  { icon: ShieldCheck,  title: "Fewer lost leads",        desc: "Automated follow-up keeps opportunities alive." },
  { icon: TrendingUp,   title: "Higher conversion",       desc: "Better process. Smarter agents. More closed deals." },
];

const WHY = [
  { icon: Building2,  title: "Purpose-built for real estate",          desc: "Designed for brokerages by real estate operators who understand your world." },
  { icon: Lock,       title: "Secure. Reliable. Enterprise-grade.",    desc: "Bank-grade security and reliable, monitored uptime you can trust, every single day." },
  { icon: Headphones, title: "Onboarding & support that scale with you", desc: "Expert support to ensure fast onboarding and sustainable growth." },
];

function SectionTitle({ children, testid }) {
  return (
    <h2 data-testid={testid}
      className="font-display text-center text-platinum text-[30px] sm:text-[36px] lg:text-[44px] leading-tight tracking-[-0.018em] fade-up">
      {children}
    </h2>
  );
}

function CardIcon({ Icon, tone = "sapphire" }) {
  // tone: sapphire (challenges + results + why) | gold (solutions)
  const isGold = tone === "gold";
  const ring   = isGold ? "rgba(217,165,79,0.55)" : "rgba(96,165,250,0.55)";
  const glow   = isGold ? "rgba(217,165,79,0.42)" : "rgba(96,165,250,0.45)";
  const fill   = isGold ? "rgba(217,165,79,0.10)" : "rgba(96,165,250,0.10)";
  const color  = isGold ? "#E5C77F" : "#60A5FA";
  return (
    <div className="w-12 h-12 rounded-full inline-flex items-center justify-center shrink-0"
      style={{
        background: `radial-gradient(circle at 30% 30%, ${fill}, #0E1422 75%)`,
        border: `1px solid ${ring}`,
        boxShadow: `0 0 18px -3px ${glow}, inset 0 0 10px ${fill}`,
      }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
  );
}

export default function BrokeragesPage() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrollY(window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  // Subtle parallax — hero skyline drifts against scroll
  const heroParallax = `translate3d(0, ${-scrollY * 0.14}px, 0)`;

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="brokerages-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={32} tone="mixed" density={0.55} />
      </div>
      <Navbar onBookDemo={() => setDemoOpen(true)} />

      {/* HERO */}
      <section className="relative pt-[120px] lg:pt-[150px] pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.06]" aria-hidden />
        <div className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.20), transparent)" }} aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center min-h-[560px] lg:min-h-[660px]">
            <div className="fade-up relative z-10">
              <Link to="/solutions" data-testid="brokerages-back"
                className="inline-flex items-center gap-2 text-[12px] text-platinum/60 hover:text-platinum mb-6 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Solutions
              </Link>
              <div className="text-[10.5px] tracking-[0.38em] uppercase font-medium">
                <span className="text-sapphire-glow">SOLUTIONS</span>
                <span className="text-platinum/40 mx-2">/</span>
                <span className="text-sapphire-glow">BROKERAGES</span>
              </div>
              <h1 className="font-display mt-5 text-platinum text-[44px] sm:text-[56px] lg:text-[64px] xl:text-[68px] leading-[1.04] tracking-[-0.022em]">
                Intelligence Built<br />
                <span style={{ textShadow: "0 0 30px rgba(96,165,250,0.25)" }}>for Brokerages</span>
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/72 leading-[1.7] max-w-[520px]">
                Capture, qualify, and convert more leads — before they go cold.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <button onClick={() => navigate("/get-your-audit-report")} data-testid="brokerages-hero-audit"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Get Your Audit Report <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => setDemoOpen(true)} data-testid="brokerages-hero-demo"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* RIGHT — skyline FULL-BLEED, homepage style */}
            <div className="relative lg:-mr-10 xl:-mr-20" data-testid="brokerages-hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.32), transparent 72%)", filter: "blur(48px)" }} aria-hidden />

              {/* Twinkling sparkles drifting over the skyline */}
              <div className="absolute top-[22%] left-[26%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.2s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[36%] right-[20%] w-1.5 h-1.5 rounded-full bg-white twinkle" style={{ animationDelay: "1.4s", boxShadow: "0 0 12px rgba(255,255,255,0.95)" }} aria-hidden />
              <div className="absolute bottom-[36%] left-[14%] w-1 h-1 rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "2.1s", boxShadow: "0 0 10px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute top-[18%] right-[36%] w-[5px] h-[5px] rounded-full bg-sapphire-glow twinkle" style={{ animationDelay: "0.7s", boxShadow: "0 0 14px rgba(96,165,250,1)" }} aria-hidden />
              <div className="absolute bottom-[44%] right-[10%] w-1 h-1 rounded-full bg-amber-200 twinkle" style={{ animationDelay: "1.6s", boxShadow: "0 0 10px rgba(252,211,77,0.9)" }} aria-hidden />

              <div className="relative">
                <img src={BROKERAGES_IMAGES.hero} alt="Esthera RealityOS — connected intelligence network across the brokerage skyline"
                  className="block w-full h-[520px] lg:h-[680px] object-cover object-center"
                  style={{
                    WebkitMaskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                    maskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  }}
                  draggable="false" />
                {/* Vertical edge fade */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)" }} aria-hidden />
                {/* Horizontal edge fade — strong fade to left into copy column */}
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.82) 14%, rgba(10,15,26,0.18) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)" }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE CHALLENGE TODAY */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <SectionTitle testid="brokerages-challenge-title">The Challenge Today</SectionTitle>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {CHALLENGES.map((c, i) => (
              <div key={c.title} data-testid={`brokerages-challenge-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up hover:border-sapphire-glow/35 transition-all duration-500 group min-h-[230px]"
                style={{
                  background: "linear-gradient(180deg, rgba(15,22,40,0.45) 0%, rgba(10,15,26,0.85) 100%)",
                  animationDelay: `${i * 80}ms`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(96,165,250,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
              >
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/35 to-transparent" />
                <CardIcon Icon={c.icon} tone="sapphire" />
                <div className="mt-5 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{c.title}</div>
                <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW ESTHERA SOLVES IT */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <SectionTitle testid="brokerages-solves-title">How Esthera Solves It</SectionTitle>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {SOLUTIONS.map((s, i) => (
              <div key={s.title} data-testid={`brokerages-solution-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-7 fade-up hover:border-amber-300/30 transition-all duration-500 min-h-[230px]"
                style={{
                  background: "linear-gradient(180deg, rgba(20,18,12,0.45) 0%, rgba(10,15,26,0.85) 100%)",
                  animationDelay: `${i * 80}ms`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(217,165,79,0.28)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
              >
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-300/35 to-transparent" />
                <CardIcon Icon={s.icon} tone="gold" />
                <div className="mt-5 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{s.title}</div>
                <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REAL RESULTS — funnel + outcomes */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <SectionTitle testid="brokerages-results-title">Real Results for Your Brokerage</SectionTitle>
          <div className="mt-10 relative rounded-2xl border border-white/[0.07] p-6 sm:p-10 lg:p-12 overflow-hidden"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.92) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/40 to-transparent" />
            <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center">
              {/* Funnel image — KEEP LABELS VISIBLE → no tight mask, just screen blend + boosted contrast */}
              <div className="relative aspect-square fade-up">
                <IllustrativePill position="top-3 right-3" z="z-20" />
                <div className="absolute inset-[-6%] pointer-events-none"
                  style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.30), transparent 72%)", filter: "blur(40px)" }} />
                <img src={BROKERAGES_IMAGES.funnel} alt="Sales funnel — Inquiries Captured → AI Qualification → Nurture & Follow-Up → Agent Engagement → Viewings Booked → Deals Closed"
                  className="relative w-full h-full object-contain breathe-glow"
                  style={{
                    mixBlendMode: "screen",
                    filter: "brightness(0.85) contrast(3.0) saturate(1.25)",
                    /* Wide elliptical mask so both the labels (left) and funnel (right) remain visible
                       while the dark navy box edges around all four sides dissolve to transparent */
                    WebkitMaskImage: "radial-gradient(ellipse 92% 88% at 50% 50%, #000 56%, rgba(0,0,0,0.5) 80%, transparent 100%)",
                    maskImage: "radial-gradient(ellipse 92% 88% at 50% 50%, #000 56%, rgba(0,0,0,0.5) 80%, transparent 100%)",
                  }}
                  draggable="false" />
              </div>

              {/* Outcome grid — 2x2 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
                {RESULTS.map((r, i) => (
                  <div key={r.title} data-testid={`brokerages-result-${i}`}
                    className="fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                    <CardIcon Icon={r.icon} tone="sapphire" />
                    <div className="mt-4 font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25]">{r.title}</div>
                    <p className="mt-2 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65] max-w-[260px]">{r.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY ESTHERA FOR BROKERAGES */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1320px] px-6 lg:px-10">
          <SectionTitle testid="brokerages-why-title">Why Esthera for Brokerages</SectionTitle>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {WHY.map((w, i) => (
              <div key={w.title} data-testid={`brokerages-why-${i}`}
                className="relative rounded-2xl border border-white/[0.07] p-6 lg:p-8 fade-up hover:border-sapphire-glow/35 transition-all duration-500 min-h-[200px]"
                style={{
                  background: "linear-gradient(180deg, rgba(15,22,40,0.45) 0%, rgba(10,15,26,0.85) 100%)",
                  animationDelay: `${i * 90}ms`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 30px -8px rgba(96,165,250,0.35)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
              >
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/35 to-transparent" />
                <div className="flex items-start gap-4">
                  <CardIcon Icon={w.icon} tone="sapphire" />
                  <div className="flex-1">
                    <div className="font-display text-platinum text-[16px] sm:text-[17px] leading-[1.25] tracking-tight">{w.title}</div>
                    <p className="mt-2.5 text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.65]">{w.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIT CTA — uses StandardCTA (uniform site-wide) */}
      <StandardCTA
        testId="brokerages-cta"
        eyebrow="GET STARTED"
        eyebrowTone="positioning"
        title="See exactly where your brokerage is leaking revenue."
        highlight="is leaking revenue."
        subtitle="Start with a free operations audit — no cost, no commitment."
        crystal={BROKERAGES_IMAGES.auditCta}
        primaryLabel="Get Your Audit Report"
        primaryHref="/get-your-audit-report"
        secondaryLabel="Book a Demo"
        secondaryHref="/book-demo"
      />

      <RelatedSolutions currentSlug="brokerages" />
      <Footer />
      <DemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
